import { useMemo } from 'react'
import { atoms as allAtoms } from '../data/atoms'

/**
 * 下部パレット：原子ボタン ＋ 結合モード選択
 */
export default function AtomPalette({
  onSelectAtom,
  allowedElements,      // ヒントモード時は必要元素だけ表示
  bondOrder,            // 現在の結合モード (1/2/3)
  onBondOrderChange,
  enableTapAdd = false,
}) {
  const visibleAtoms = useMemo(() => {
    if (!allowedElements?.length) return allAtoms
    const allow = new Set(allowedElements)
    return allAtoms.filter((a) => allow.has(a.element))
  }, [allowedElements])

  const bondModes = [
    { order: 1, label: '単結合', symbol: '—' },
    { order: 2, label: '二重結合', symbol: '＝' },
    { order: 3, label: '三重結合', symbol: '≡' },
  ]

  return (
    <div className="p-3 md:p-4 h-full overflow-y-auto">
      {/* 結合モード */}
      <div className="mb-3">
        <p className="text-[10px] font-bold text-white/45 uppercase tracking-wider mb-1.5">
          結合モード（原子どうしをつなぐ線の種類）
        </p>
        <div className="flex gap-1.5">
          {bondModes.map((m) => {
            const active = bondOrder === m.order
            return (
              <button
                key={m.order}
                onClick={() => onBondOrderChange?.(m.order)}
                className="flex-1 py-2 rounded-lg font-bold text-xs transition-all"
                style={{
                  background: active ? 'rgba(52,211,153,0.2)' : 'rgba(255,255,255,0.05)',
                  color: active ? '#34D399' : 'rgba(255,255,255,0.55)',
                  border: `1px solid ${active ? 'rgba(52,211,153,0.5)' : 'transparent'}`,
                }}
              >
                <span className="text-base mr-1">{m.symbol}</span>
                {m.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* 原子 */}
      <p className="text-[10px] font-bold text-white/45 uppercase tracking-wider mb-1.5">
        {enableTapAdd ? '原子（タップで追加）' : '原子（ドラッグで配置）'}
      </p>
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
        {visibleAtoms.map((atom) => {
          const isLight = ['#E6E6E6', '#FFFFFF'].includes(atom.color.toUpperCase())
          return (
            <div
              key={atom.element}
              draggable={!enableTapAdd}
              onClick={() => {
                if (!enableTapAdd) return
                onSelectAtom?.(atom)
              }}
              onTouchEnd={(e) => {
                if (!enableTapAdd) return
                e.preventDefault()
                onSelectAtom?.(atom)
              }}
              onDragStart={(e) => {
                if (enableTapAdd) return
                e.dataTransfer.setData('application/chemcraft', JSON.stringify(atom))
                e.dataTransfer.effectAllowed = 'move'
              }}
              className="flex flex-col items-center gap-1 cursor-pointer md:cursor-grab md:active:cursor-grabbing select-none transition-transform hover:scale-105"
              title={atom.name}
            >
              <div
                className="flex items-center justify-center rounded-full shadow-lg"
                style={{
                  width: 40,
                  height: 40,
                  background: `radial-gradient(circle at 35% 30%, ${atom.color}, ${atom.color}CC)`,
                  border: '2px solid rgba(255,255,255,0.3)',
                  color: isLight ? '#1a1f35' : '#fff',
                  fontSize: 15,
                  fontWeight: 900,
                }}
              >
                {atom.element}
              </div>
              <span className="text-white/60 text-[8px] font-semibold text-center leading-tight">
                {atom.name}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
